import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { Link, Navigate, Outlet, useParams } from "@tanstack/react-router";
import { Storefront } from "@phosphor-icons/react";
import { Button, EmptyState, Select } from "@ghezaa/ui";
import { NotFound } from "@ghezaa/shell";
import type { Market, MarketsAnswer } from "@/api/client";
import { useMarkets } from "@/api/queries";

const STORAGE_KEY = "sales.market";

function remembered(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

type MarketContextValue = {
  markets: Market[];
  answer: MarketsAnswer | undefined;
  /** The market the address names (`/eg/…`), when it is one of the person's; null on any other address. */
  current: Market | null;
  /** Where `/` goes: the market on screen, else the one this browser used last, else the first. */
  home: Market | null;
  loaded: boolean;
};

const MarketContext = createContext<MarketContextValue | null>(null);

/** Which market the screens are looking at: the address's first segment, owned by the router. The browser only remembers the last one. */
export function MarketProvider({ children }: { children: ReactNode }) {
  const markets = useMarkets();
  const { market: code } = useParams({ strict: false }) as { market?: string };
  const list = useMemo(() => markets.data?.markets ?? [], [markets.data]);
  const current = (code ? list.find((m) => m.code === code) : undefined) ?? null;

  useEffect(() => {
    if (!current) return;
    try {
      localStorage.setItem(STORAGE_KEY, current.code);
    } catch {
      // private mode or blocked storage: `/` then opens the first market
    }
  }, [current]);

  const value = useMemo(() => {
    const last = remembered();
    const home = current ?? list.find((m) => m.code === last) ?? list[0] ?? null;
    return { markets: list, answer: markets.data, current, home, loaded: !markets.isPending };
  }, [list, current, markets.data, markets.isPending]);
  return <MarketContext.Provider value={value}>{children}</MarketContext.Provider>;
}

export function useMarket() {
  const value = useContext(MarketContext);
  if (!value) throw new Error("useMarket must be used inside MarketProvider");
  return value;
}

/** The market on screen, inside a market's pages (MarketGate guarantees it). */
export function useCurrentMarket(): Market {
  const { current } = useMarket();
  if (!current) throw new Error("useCurrentMarket must be used inside a market's pages");
  return current;
}

/** `/`: the market this browser used last, or the first; with none, what to do about it. */
export function MarketEntry() {
  const { home, loaded, answer } = useMarket();
  if (!loaded) return <div className="h-40 bg-surface-2" aria-busy="true" />;
  if (home) {
    // A manager starts on the team, a salesperson on their day; an administrator on what they may open.
    const to = home.can.team ? "/$market/team" : home.can.own ? "/$market/today" : home.can.erp ? "/$market/business-central" : "/$market/people";
    return <Navigate to={to} params={{ market: home.code }} replace />;
  }
  if (answer?.canSetUp) return <Navigate to="/markets" replace />;
  return (
    <EmptyState
      icon={Storefront}
      title="No market to show you"
      description="Sales shows the markets where one of your roles gives you something to see. Ask a Sales admin for a role in your market."
    />
  );
}

/** The pages of one market: shown when the address names one of the person's markets, not found for any other code. */
export function MarketGate() {
  const { current, loaded } = useMarket();
  if (current) return <Outlet />;
  if (!loaded) return <div className="h-40 bg-surface-2" aria-busy="true" />;
  return (
    <NotFound
      action={
        <Button asChild>
          <Link to="/">Open Sales</Link>
        </Button>
      }
    />
  );
}

/** The market switch at the top of the sidebar: every market page shows this market. Switching changes the address. */
export function MarketSwitch({ onSwitch }: { onSwitch: (code: string) => void }) {
  const { markets, current, home } = useMarket();
  if (markets.length === 0) return null;
  return (
    <div className="flex flex-col">
      <label htmlFor="market-switch" className="px-2 pb-1 text-2xs font-medium uppercase tracking-wider text-ink-3">
        Market
      </label>
      <Select id="market-switch" value={(current ?? home)?.code ?? ""} onChange={(e) => onSwitch(e.target.value)} className="h-8 text-sm">
        {markets.map((m) => (
          <option key={m.code} value={m.code}>
            {m.name} · {m.currency}
          </option>
        ))}
      </Select>
    </div>
  );
}
